import React from 'react';
import { Box, IconButton, LinearProgress, Typography, useTheme } from '@mui/material';
import { getScoreColor } from './driver-score-utils';
import DriverScoreModal from '../../modal/DriverScoreModal';
import InfoIcon from '@mui/icons-material/Info';
import { useHandleVisible } from '../../../utils/handle-visible';

/**
 * Props for the DriverScoreRow component.
 */
interface DriverScoreRowProps {
	/** Label describing the score metric (e.g., "Smoothness", "Speed", "Usage"). */
	label: string;
	/** Score value (0-100) to display and visualize in the progress bar. */
	score: number;
}

/**
 * Individual score metric row with label, value, and progress bar.
 *
 * Displays a single driver score metric with the label on the left,
 * numeric score on the right, and a colored progress bar below.
 * The progress bar color indicates performance level.
 *
 * @component
 * @example
 * ```tsx
 * <DriverScoreRow label="Smoothness" score={78} />
 * <DriverScoreRow label="Speed" score={45} />
 * ```
 */
const DriverScoreRow: React.FC<DriverScoreRowProps> = ({ label, score }) => {
	const theme = useTheme();
	const { visible, handleVisible, handleInvisible } = useHandleVisible();
	const color = getScoreColor(score, theme);

	return (
		<>
			<Box sx={{ mb: 2 }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
					<Typography variant='body2' sx={{ fontWeight: 500 }}>
						{label}
					</Typography>
					<Typography
						variant='body2'
						component='span'
						sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}
					>
						{Math.round(score)}
						<IconButton
							size='small'
							onClick={handleVisible}
							sx={{ p: 0 }}
							aria-label={`Driver score ${label} information`}
						>
							<InfoIcon
								sx={{
									fontSize: 20,
									color: theme.palette.success.main,
								}}
							/>
						</IconButton>
					</Typography>
				</Box>
				<LinearProgress
					variant='determinate'
					value={score}
					sx={{
						height: 8,
						borderRadius: 4,
						backgroundColor: theme.palette.grey[300],
						'& .MuiLinearProgress-bar': {
							backgroundColor: color,
							borderRadius: 4,
						},
					}}
				/>
			</Box>
			<DriverScoreModal open={visible} onClose={handleInvisible} label={label} />
		</>
	);
};

export default DriverScoreRow;
